import { useEffect, useRef } from 'react';

/**
 * Hook for determining whether or not the component has been committed. This is usually the same as
 * whether or not this component has been rendered, with some caveats.
 *
 * It is possible for a function component to be rendered more than once before it is committed,
 * e.g., in React's strict mode or concurrent mode. Thus, this hook may return `true` more than once
 * for a specific component instance if the initial render phase runs more than once before the
 * commit phase.
 *
 * If you want to only do some work when a component initially mounts, it is generally safer to
 * check whether the component has been committed rather than merely rendered, especially if the
 * work causes side effects or depends on values (like props) that may be different for subsequent
 * renders for the same commit.
 *
 * @see https://reactjs.org/docs/strict-mode.html#detecting-unexpected-side-effects
 */
const useHasComponentCommitted = () => {
  const hasCommitted = useRef(false);

  // We only update `hasCommitted` inside an effect, which runs during the commit phase, in order to
  // handle cases where the render phase could run more than once before the initial commit phase.
  useEffect(() => {
    hasCommitted.current = true;
  }, []);

  return hasCommitted.current;
};

/**
 * Hook for returning a ref whose initial value is only evaluated on the first render.
 *
 * Note that it is still possible for the provided getter to be evaluated more than once when a
 * component mounts, e.g., in React's strict mode or concurrent mode.
 *
 * @param valueGetter A function that returns the initial ref value.
 */
export const useLazyRef = <T>(valueGetter: () => T) => {
  const didInitialize = useHasComponentCommitted();

  const ref = useRef<T>();
  if (!didInitialize) {
    ref.current = valueGetter();
  }

  return ref as React.MutableRefObject<T>;
};
